import { DeleteOutlined, EditOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons'
import { Button, Dropdown, MenuProps } from 'antd'
import classNames from 'classnames'
import React, { useContext, useEffect, useState } from 'react'
import { Responsive, WidthProvider } from 'react-grid-layout'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { Link } from 'react-router-dom'
import PageHeaderWrapper from '../../../components/PageHeaderWrapper/PageHeaderWrapper'
import { contextCompanyIdSelector } from '../../../redux/context/company/selectors'
import {
  updateCompanyDashboardLayouts,
  deleteCompanyDashboardRequest,
  getDataForCompanyDashboard,
  getCompanyDashboardRequest
} from '../../../redux/context/dashboard/company/actions'
import {
  companyDashboardLayoutsSelector,
  companyDashboardLoadingSelector,
  companyDashboardSelector
} from '../../../redux/context/dashboard/company/selector'
import GridItem from './components/GridItem'
import DashboardModal from './components/DashboardModal'
import { filtersSelector } from '../../../redux/context/filters/selectors'
import DocumentationLink from '../../../components/Misc/DocumentationLink'
import { NewDasboadItemModal } from './components/DashboardItemEdit/DasboadItemModal'
import {
  CompanyDashboardContextType,
  CompanyDashboardItemContext
} from './components/DashboardItemEdit/DashboardItemProvider'
import { setActiveDashboard } from '../../../redux/pages/dashboard/companyDashboard/actions'
import { companyDashboardPageSelector } from '../../../redux/pages/dashboard/companyDashboard/selectors'
import { AppDispatch } from '../../../redux/store'
import ReportSelect from '../../../components/Misc/ReportSelect'
import './_CompanyDashboard.sass'
import { resetReports } from '../../../redux/context/reports/actions'
import { useCtxDimensions } from '../../../features/dimension/queries/useCtxDimensions'
import { useDimensionQuery } from '../../../features/dimension/hooks/hooks'
import { useDeleteConfirmModal } from '../../../shared/modals'

const ResponsiveGridLayout = WidthProvider(Responsive)

const CompanyDashboard: React.FC = () => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useDispatch()
  const deleteConfirmModal = useDeleteConfirmModal()

  const companyId = useSelector(contextCompanyIdSelector)
  const { activeDashboard: activeDashboardId } = useSelector(companyDashboardPageSelector)
  const companyDashboards = useSelector(companyDashboardSelector)
  const { budgetingScenario, dimensions, periodGroups } = useSelector(filtersSelector)
  const companyDashboardLayoutsUnfiltered = useSelector(companyDashboardLayoutsSelector)
  const companyDashboardLoading = useSelector(companyDashboardLoadingSelector)
  const companyDashboardLayouts = _.cloneDeep(companyDashboardLayoutsUnfiltered)
  companyDashboardLayouts && delete companyDashboardLayouts.lastModified
  const [modalVisible, setModalVisible] = useState<'edit' | 'new' | false>(false)
  const activeDashboard = companyDashboards?.find(c => c.id === Number(activeDashboardId))
  const { isLoading: dimensionsLoading } = useCtxDimensions()
  const dimensionQuery = useDimensionQuery()

  const { handleOpen } = useContext(CompanyDashboardItemContext) as CompanyDashboardContextType

  const initData = () => {
    if (companyDashboards && companyDashboards[0] && !activeDashboard) {
      dispatch(setActiveDashboard(companyDashboards[0].id))
    }
  }

  const getDashboards = () => {
    companyId && dispatch(getCompanyDashboardRequest(companyId))
  }

  const getDashboardData = () => {
    // TODO: loading hässäkkä
    dispatch(resetReports())

    if ((activeDashboardId && dimensions?.length && !dimensionsLoading) || (activeDashboardId && !dimensions?.length)) {
      dispatch(getDataForCompanyDashboard(activeDashboard))
    }
  }

  useEffect(getDashboards, [companyId, budgetingScenario, dimensions, periodGroups])
  useEffect(initData, [companyDashboards, budgetingScenario, dimensions, periodGroups, dispatch])
  useEffect(getDashboardData, [activeDashboardId, budgetingScenario, dimensionQuery, periodGroups, dispatch])

  const deleteDashboard = async () => {
    if (companyId && activeDashboard && activeDashboardId) {
      dispatch(deleteCompanyDashboardRequest(companyId, activeDashboardId))
    }
    dispatch(setActiveDashboard(null))
  }

  const handleMenuClick = ({ key }: any) => {
    switch (key) {
      case 'edit':
        setModalVisible('edit')
        break
      case 'delete':
        deleteConfirmModal({
          content: activeDashboard?.name,
          onOk: () => deleteDashboard()
        })
        break
      default:
        break
    }
  }

  const onChange = (id: number) => {
    dispatch(setActiveDashboard(id))
  }

  const reportButtonItems: MenuProps['items'] = [
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: t('global:edit')
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: t('global:delete')
    }
  ]

  return (
    <PageHeaderWrapper
      title={t('menu:/settings/accountant/subscriptions/company-dashboard')}
      subTitle={<DocumentationLink />}
      extra={[
        <ReportSelect activeDashboard={activeDashboardId} dashboards={companyDashboards} onChange={onChange} />,
        <Link to="/settings/dashboards/company/order">
          <Button icon={<SettingOutlined />}>{t('global:settings')}</Button>
        </Link>,
        <Button key="new" icon={<PlusOutlined />} onClick={() => setModalVisible('new')}>
          {t('dashboardPage:new-dashboard')}
        </Button>,
        <Dropdown.Button
          key="dropDown"
          menu={{ items: reportButtonItems, onClick: handleMenuClick }}
          disabled={!activeDashboard}
          onClick={() => {
            handleOpen()
          }}
        >
          <PlusOutlined />
          {t('dashboardPage:add-new-item')}
        </Dropdown.Button>
      ]}
      ghost={false}
    >
      <DashboardModal dashboard={activeDashboard} visible={modalVisible} setVisible={setModalVisible} key="1" />
      <NewDasboadItemModal />
      <ResponsiveGridLayout
        isResizable
        className={classNames('layout', { animated: !companyDashboardLoading })}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
        rowHeight={30}
        margin={[16, 16]}
        style={{ position: 'relative', margin: -16 }}
        draggableHandle=".grid-item-header"
        layouts={activeDashboard && (activeDashboard.layouts as ReactGridLayout.Layouts)}
        onLayoutChange={(currentLayout, allLayouts) => {
          if (!_.isEqual(companyDashboardLayouts, allLayouts)) {
            activeDashboard &&
              companyId &&
              !companyDashboardLoading &&
              dispatch(updateCompanyDashboardLayouts(activeDashboard, allLayouts, currentLayout, companyId))
          }
        }}
      >
        {activeDashboard?.items?.map(item => {
          return (
            item && (
              <div
                key={item.id?.toString()}
                data-grid={
                  activeDashboard.layouts?.['lg' || activeDashboard.layouts?.lastModified]?.find(
                    f => Number(f.i) === item.id
                  ) || {
                    x: 0,
                    y: Infinity,
                    w: 6,
                    h: 10
                  }
                }
              >
                <GridItem item={item} companyDashboardId={activeDashboard.id} />
              </div>
            )
          )
        })}
      </ResponsiveGridLayout>
    </PageHeaderWrapper>
  )
}

export default CompanyDashboard
